/* eslint-disable */
/**
 * PrefundSLAConfirmation — feature reusable Prefund SLA gate panel (RACV-308).
 *
 * Rendered between "total cost resolved" (EligibilityDecision's onPricingResolved) and
 * the existing generateFunding call inside CapturedImage.tsx. Shows the SLA decision
 * (WITHIN_STORE_LIMIT / REQUIRES_MANAGER_PIN / EXCEEDS_DM_LIMIT) and gates
 * "Confirm Request" behind manager PIN approval when required.
 */
import React, { useState, useEffect } from 'react';
import {
  makeStyles,
  Typography,
  Grid,
} from '@rentacenter/racstrap';
import SecondFactor from '../Rental/TwoFactorPopup/TwoFactorPopup';
import { PrefundSLADecision } from './validatePrefundSLA';

export interface PrefundSLALineItem {
  productName: string;
  quantity: string;
  unitCost: string;
}

export interface PrefundSLAConfirmationProps {
  lineItems: PrefundSLALineItem[];
  subtotal: number;
  fudgeAmount: number; // totalWithFudge - subtotal
  variancePercent: number; // VarianceThreshold
  totalWithFudge: number;
  storeLimit: number | null;
  dmLimit: number | null;
  decision: PrefundSLADecision;
  slaApproved: boolean;
  currentRole: string;
  onConfirmRequest: () => void; // calls generateFunding upstream
  onSlaApproved: () => void;   // sets slaApproved = true upstream
  onSlaCancelled: () => void;  // called when user dismisses PIN modal via Cancel or X
}

const FONT_CHOICE = 'OpenSans-semibold';

const useStyles = makeStyles(() => ({
  root: {
    display: 'flex',
    flexDirection: 'column' as const,
    width: '100%',
    height: '100%',
    padding: '0.5rem 0.75rem',
    overflowY: 'auto' as const,
  },
  lineItemRow: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '0.75rem',
    fontFamily: 'OpenSans-regular',
    color: '#333333',
    padding: '0.25rem 0',
    borderBottom: '1px solid #EEEEEE',
  },
  summaryRow: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '0.75rem',
    fontFamily: 'OpenSans-regular',
    color: '#333333',
    padding: '0.2rem 0',
  },
  totalRow: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '0.875rem',
    fontFamily: FONT_CHOICE,
    fontWeight: 700,
    color: '#111111',
    padding: '0.375rem 0',
    marginTop: '0.25rem',
    borderTop: '2px solid #DDDDDD',
  },
  limitsRow: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '0.7rem',
    fontFamily: 'OpenSans-regular',
    color: '#555555',
    padding: '0.15rem 0',
  },
  bannerGreen: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    backgroundColor: '#E8F5E9',
    borderLeft: '4px solid #2E7D32',
    borderRadius: '4px',
    padding: '0.5rem 0.75rem',
    marginTop: '0.5rem',
    marginBottom: '0.5rem',
  },
  bannerGreenText: {
    fontSize: '0.75rem',
    fontFamily: FONT_CHOICE,
    color: '#2E7D32',
  },
  bannerAmber: {
    display: 'flex',
    flexDirection: 'column' as const,
    backgroundColor: '#FFF8E1',
    borderLeft: '4px solid #F9A825',
    borderRadius: '4px',
    padding: '0.5rem 0.75rem',
    marginTop: '0.5rem',
    marginBottom: '0.5rem',
  },
  bannerAmberText: {
    fontSize: '0.75rem',
    fontFamily: 'OpenSans-regular',
    color: '#8A6100',
    lineHeight: 1.4,
  },
  bannerRed: {
    display: 'flex',
    flexDirection: 'column' as const,
    backgroundColor: '#FDECEA',
    borderLeft: '4px solid #C62828',
    borderRadius: '4px',
    padding: '0.5rem 0.75rem',
    marginTop: '0.5rem',
    marginBottom: '0.5rem',
  },
  bannerRedText: {
    fontSize: '0.75rem',
    fontFamily: 'OpenSans-regular',
    color: '#C62828',
    lineHeight: 1.4,
  },
  approvedBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    backgroundColor: '#E8F5E9',
    borderLeft: '4px solid #2E7D32',
    borderRadius: '4px',
    padding: '0.5rem 0.75rem',
    marginTop: '0.5rem',
    marginBottom: '0.5rem',
  },
  approvedBadgeText: {
    fontSize: '0.75rem',
    fontFamily: FONT_CHOICE,
    color: '#2E7D32',
  },
  actionsRow: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '0.5rem',
    marginTop: '0.75rem',
  },
}));

function formatCurrency(value: number | null): string {
  if (value === null || Number.isNaN(value)) return 'Unavailable';
  return value.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
  });
}

/**
 * PrefundSLAConfirmation renders the SLA gate UI described in SD section 4.4:
 * line items, subtotal, fudge line, Total Financing Amount, Store Limit, DM Limit,
 * plus a decision-specific banner and action buttons.
 */
export const PrefundSLAConfirmation: React.FC<PrefundSLAConfirmationProps> = ({
  lineItems,
  subtotal,
  fudgeAmount,
  variancePercent,
  totalWithFudge,
  storeLimit,
  dmLimit,
  decision,
  slaApproved,
  currentRole,
  onConfirmRequest,
  onSlaApproved,
  onSlaCancelled,
}) => {
  const classes = useStyles();

  // true once the user has cancelled the PIN modal at least once — switches the
  // amber banner to the "re-enter or reduce amount" message and shows the link.
  const [slaCancelled, setSlaCancelled] = useState<boolean>(false);

  // PIN modal auto-opens when decision is REQUIRES_MANAGER_PIN and not yet approved.
  const [slaModalOpen, setSlaModalOpen] = useState<boolean>(
    decision === 'REQUIRES_MANAGER_PIN' && !slaApproved
  );

  const isWithinStoreLimit = decision === 'WITHIN_STORE_LIMIT';
  const requiresManagerPin = decision === 'REQUIRES_MANAGER_PIN';
  const exceedsDmLimit = decision === 'EXCEEDS_DM_LIMIT';
  // Store/DM limits could not be retrieved from the config service — there is no
  // valid amount to compare against, so the request must be hard-blocked just like
  // exceeding the DM limit. No SLA override path is offered for this case either.
  const configUnavailable = decision === 'CONFIG_UNAVAILABLE';

  // Also re-open if decision changes to REQUIRES_MANAGER_PIN after initial render
  useEffect(() => {
    if (requiresManagerPin && !slaApproved) {
      setSlaModalOpen(true);
    }
  }, [requiresManagerPin, slaApproved]);

  // TwoFactorPopup calls setTwoFactorCompleted(true) on success AND
  // setTwoFactorCompleted(false) on Cancel / X — check the boolean.
  const handleTwoFactorCompleted = (success: boolean) => {
    if (!success) {
      setSlaModalOpen(false);
      setSlaCancelled(true);
      onSlaCancelled();
      return;
    }
    setSlaModalOpen(false);
    onSlaApproved();
  };

  const handleTwoFactorCancelClick = () => {
    setSlaModalOpen(false);
    setSlaCancelled(true);
    onSlaCancelled();
  };

  return (
    <div className={classes.root}>
      <Typography style={{ fontSize: '0.875rem', fontWeight: 700, fontFamily: FONT_CHOICE, color: '#111', marginBottom: '0.5rem' }}>
        Prefund SLA Confirmation
      </Typography>

      {/* Line items */}
      {lineItems.map((item, index) => (
        <div key={index} className={classes.lineItemRow}>
          <span>{`${item.productName} × ${item.quantity}`}</span>
          <span>{formatCurrency(Number(item.unitCost) || 0)}</span>
        </div>
      ))}

      {/* Subtotal + fudge line */}
      <div className={classes.summaryRow}>
        <span>Subtotal</span>
        <span>{formatCurrency(subtotal)}</span>
      </div>
      <div className={classes.summaryRow}>
        <span>{`Variance (${variancePercent}%)`}</span>
        <span>{formatCurrency(fudgeAmount)}</span>
      </div>

      {/* Total Financing Amount */}
      <div className={classes.totalRow}>
        <span>Total Financing Amount</span>
        <span>{formatCurrency(totalWithFudge)}</span>
      </div>

      {/* Store Limit / DM Limit */}
      <div className={classes.limitsRow}>
        <span>Store Limit</span>
        <span>{formatCurrency(storeLimit)}</span>
      </div>
      <div className={classes.limitsRow}>
        <span>DM Limit</span>
        <span>{formatCurrency(dmLimit)}</span>
      </div>

      {/* Decision-specific banner */}
      {isWithinStoreLimit && (
        <div className={classes.bannerGreen}>
          <Typography className={classes.bannerGreenText}>
            {'✓ Within Store Limit'}
          </Typography>
        </div>
      )}

      {requiresManagerPin && !slaApproved && (
        <div className={classes.bannerAmber}>
          <Typography className={classes.bannerAmberText}>
            {slaCancelled
              ? 'Manager PIN was not entered. To proceed, have a manager enter their PIN — or reduce the cart total below the store limit.'
              : 'This request exceeds the store prefund limit and requires manager approval before it can proceed. Have a manager enter their PIN to override.'}
          </Typography>
        </div>
      )}

      {requiresManagerPin && slaApproved && (
        <div className={classes.approvedBadge}>
          <Typography className={classes.approvedBadgeText}>
            {'✓ Manager Override Approved'}
          </Typography>
        </div>
      )}

      {exceedsDmLimit && (
        <div className={classes.bannerRed}>
          <Typography className={classes.bannerRedText}>
            {'This request exceeds the maximum funding limit. Please remove items to reduce the cost below the DM limit and try again.'}
          </Typography>
        </div>
      )}

      {configUnavailable && (
        <div className={classes.bannerRed}>
          <Typography className={classes.bannerRedText}>
            {'Store and District Manager prefund limits could not be retrieved from the config service. This request cannot proceed until the limits are available. Please try again or contact support.'}
          </Typography>
        </div>
      )}

      {/* Action row:
          - Before cancel: hidden (PIN popup auto-opens, no extra button needed).
          - After cancel: "Re-enter Manager PIN" link reopens the modal.
            The while-loop in CapturedImage is still live and re-arms on each
            iteration, so a successful PIN here resumes the flow normally. */}
      <Grid className={classes.actionsRow}>
        {requiresManagerPin && !slaApproved && slaCancelled && (
          <Typography
            style={{ fontSize: '0.75rem', color: '#2979FF', cursor: 'pointer', textDecoration: 'underline' }}
            onClick={() => {
              setSlaCancelled(false);
              setSlaModalOpen(true);
            }}
          >
            {'Re-enter Manager PIN'}
          </Typography>
        )}
      </Grid>

      {/* SLA PIN modal — SecondFactor renders its own RACModalCard internally */}
      {slaModalOpen && (
        <SecondFactor
          moduleName="pastDuePin"
          currentRole={currentRole}
          setTwoFactorCancelClick={handleTwoFactorCancelClick}
          setTwoFactorCompleted={handleTwoFactorCompleted}
        />
      )}
    </div>
  );
};

export default PrefundSLAConfirmation;
